<template>
  <div
    v-if="show"
    class="fixed z-[9999] inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center p-0 sm:p-4"
  >
    <div
      class="bg-white w-full sm:max-w-md max-h-screen sm:max-h-[95vh] sm:rounded-lg border-0 sm:border border-gray-200 relative animate-fade-in flex flex-col shadow-2xl"
    >
      <!-- Modal Loading Overlay -->
      <div
        v-if="isLoadingClasses || isPromoting"
        class="absolute inset-0 bg-white bg-opacity-80 z-10 flex items-center justify-center sm:rounded-lg"
      >
        <div class="text-center">
          <div
            class="w-6 h-6 sm:w-8 sm:h-8 border-3 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-2"
          ></div>
          <p class="text-xs sm:text-sm text-gray-600">
            {{
              isLoadingClasses ? "Loading classes..." : "Promoting students..."
            }}
          </p>
        </div>
      </div>

      <!-- Fixed Header -->
      <div
        class="flex-shrink-0 p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-green-100"
      >
        <button
          @click="$emit('close')"
          :disabled="isLoadingClasses || isPromoting"
          class="absolute top-2 right-2 sm:top-3 sm:right-3 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed z-10"
          title="Close"
        >
          <X class="w-5 h-5 sm:w-4 sm:h-4" />
        </button>
        <div class="flex items-center gap-2 sm:gap-3 pr-10 sm:pr-12">
          <div
            class="p-2 sm:p-3 bg-green-600 rounded-lg border border-green-600 flex-shrink-0 shadow-md"
          >
            <TrendingUp class="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          <div class="flex-1 min-w-0">
            <h2 class="text-lg sm:text-xl font-bold text-gray-800 truncate">
              Promote Students
            </h2>
            <p class="text-gray-600 text-xs sm:text-sm">
              Promote {{ selectedStudentsCount }} selected student{{
                selectedStudentsCount > 1 ? "s" : ""
              }}
              to a higher class.
            </p>
          </div>
        </div>
      </div>

      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
        <div
          v-if="errorMessage"
          class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
        >
          {{ errorMessage }}
        </div>

        <div class="space-y-4">
          <!-- Select Class -->
          <div class="space-y-2">
            <label class="block text-xs sm:text-sm font-semibold text-gray-700">
              Select Target Class <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <select
                v-model="selectedClassId"
                :disabled="isLoadingClasses || isPromoting"
                class="w-full rounded-lg border-2 border-gray-300 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-200 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium bg-white text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed appearance-none pr-10 shadow-sm"
              >
                <option value="">-- Select a class --</option>
                <option
                  v-for="cls in availableClasses"
                  :key="cls._id"
                  :value="cls._id"
                >
                  {{ cls.name }}
                  <span v-if="cls.section"> - {{ cls.section }}</span>
                </option>
              </select>
              <div
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
              >
                <svg
                  class="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <!-- Extra Fields: Only for Passout -->
          <div
            v-if="selectedClass?.name?.toLowerCase() === 'passout'"
            class="space-y-3"
          >
            <div>
              <label
                class="block text-xs sm:text-sm font-semibold text-gray-700"
              >
                Session <span class="text-red-500"></span><span>-optional</span>
              </label>
              <input
                v-model="session"
                type="text"
                placeholder="e.g. Winter, Summer, Autumn"
                class="w-full rounded-lg border-2 border-gray-300 px-3 py-2.5 focus:ring-2 focus:ring-green-200 focus:border-green-500 text-sm md:text-base"
              />
            </div>
            <div>
              <label
                class="block text-xs sm:text-sm font-semibold text-gray-700"
              >
                Year <span class="text-red-500"></span><span>-optional</span>
              </label>
              <input
                v-model="year"
                type="number"
                placeholder="e.g. 2025"
                class="w-full rounded-lg border-2 border-gray-300 px-3 py-2.5 focus:ring-2 focus:ring-green-200 focus:border-green-500 text-sm md:text-base"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex-shrink-0 p-4 sm:p-6 border-t border-gray-200 bg-white flex justify-end gap-2 sm:gap-3"
      >
        <button
          @click="$emit('close')"
          :disabled="isLoadingClasses || isPromoting"
          class="px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all duration-200 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300"
        >
          Cancel
        </button>
        <button
          @click="confirmPromotion"
          :disabled="!selectedClassId || isLoadingClasses || isPromoting"
          class="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-all duration-200 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed border border-green-600"
        >
          <div
            v-if="isPromoting"
            class="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
          ></div>
          <TrendingUp
            v-else
            class="w-3 h-3 sm:w-4 sm:h-4"
          />
          <span>{{ isPromoting ? "Promoting..." : "Confirm Promotion" }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed, watch } from "vue";
  import { useStore } from "vuex";
  import { TrendingUp, X } from "lucide-vue-next";

  const store = useStore();

  const props = defineProps({
    show: Boolean,
    selectedStudentsCount: Number,
    isPromoting: Boolean,
  });

  const emit = defineEmits(["close", "confirm-promote"]);

  const selectedClassId = ref("");
  const session = ref("");
  const year = ref("");
  const isLoadingClasses = ref(false);
  const errorMessage = ref("");

  const availableClasses = computed(() => store.getters.allClasses || []);
  const selectedClass = computed(() =>
    availableClasses.value.find((c) => c._id === selectedClassId.value)
  );

  const fetchClasses = async () => {
    isLoadingClasses.value = true;
    errorMessage.value = "";
    try {
      await store.dispatch("fetchAllClasses");
    } catch (err) {
      console.error("Failed to fetch classes for promotion modal:", err);
      errorMessage.value = "Failed to load classes. Please try again.";
    } finally {
      isLoadingClasses.value = false;
    }
  };

  const confirmPromotion = () => {
    if (!selectedClassId.value) {
      errorMessage.value = "Please select a class to promote students to.";
      return;
    }

    // if (selectedClass.value?.name?.toLowerCase() === "passout") {
    //   if (!session.value || !year.value) {
    //     errorMessage.value =
    //       "Please provide session and year for passout students.";
    //     return;
    //   }
    // }

    emit("confirm-promote", {
      classId: selectedClassId.value,
      session: session.value || null,
      year: year.value ? Number(year.value) : null,
    });
  };

  onMounted(() => {
    if (props.show) {
      fetchClasses();
      selectedClassId.value = "";
      session.value = "";
      year.value = "";
      errorMessage.value = "";
    }
  });

  watch(
    () => props.show,
    (newVal) => {
      if (newVal) {
        fetchClasses();
        selectedClassId.value = "";
        session.value = "";
        year.value = "";
        errorMessage.value = "";
      }
    }
  );
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .animate-fade-in {
    animation: fade-in 0.3s ease-out;
  }
  .touch-manipulation {
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
</style>
